import { defineComponent as _defineComponent } from 'vue'
import { computed, getCurrentInstance, ref, defineAsyncComponent } from 'vue';
import { datadogRum } from '@datadog/browser-rum';

// store
import { useTravelInsuranceStore, type Traveller } from '@white-label-store/travel-insurance';

// helpers
import { useCountryOptions } from '@white-label-helper/shared-composables';

// components
import {
  DateInput,
  Tooltip,
  SingleSelect,
} from 'ui-shared-components';
import IconButton from '@white-label-icon/icon-button';
import { TRAVEL_INSURANCE_AGE_DURATION_LIMITS } from '@white-label-configuration/constants';


export default /*#__PURE__*/_defineComponent({
  __name: 'travel-insurance-form-step-3',
  props: {
    travellers: { default: () => [] },
    defaultCountry: { default: () => '' },
    hideTooltip: { type: Boolean, default: false },
    tripEndDate: { default: '' },
    tripDuration: { default: null }
  },
  emits: ["update:value", "update:tooltip-visible"],
  setup(__props: any, { expose, emit }: { emit: ({
  (e: 'update:value', val: Traveller[]): void
  (e: 'update:tooltip-visible', val: boolean): void
}), expose: any, slots: any, attrs: any }) {

const props = __props as { travellers: Traveller[], defaultCountry: string, hideTooltip: boolean, tripEndDate: Date | string, tripDuration: number | null };

const IconS3 = defineAsyncComponent(() => import('@white-label-icon/icons-s3'));
const instance = getCurrentInstance();
const proxy = instance?.proxy as Vue;
const travelInsuranceStore = useTravelInsuranceStore();
const { countryOptions } = useCountryOptions();



const travellerDateValidity = ref<Record<string, boolean>>({});
const submittedTravellers = ref<string[]>([]);

// Local reactive copy of travellers to avoid mutating props
const localTravellers = ref<Traveller[]>([]);
// Whether the form has been touched
const dirty = ref<boolean>(false);

const allCountries = computed(() => {
  return countries.map((country: {name: string, code: string}) => ({
    name: country.name,
    id: country.code,
  }));
});

const getAgeAtTripEnd = (dateOfBirth: Date | string): number => {
  const referenceDate = props.tripEndDate ? new Date(props.tripEndDate) : new Date();
  const birthDate = new Date(dateOfBirth);
  let age = referenceDate.getFullYear() - birthDate.getFullYear();
  const monthDiff = referenceDate.getMonth() - birthDate.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && referenceDate.getDate() < birthDate.getDate())) {
    age--;
  }
  return age;
};

const showTripDurationError = (traveller: Traveller) => {
  if (!traveller.dateOfBirth || props.tripDuration == null) return;

  const age = getAgeAtTripEnd(traveller.dateOfBirth);
  const rule = TRAVEL_INSURANCE_AGE_DURATION_LIMITS.find(r => age >= r.minAge && age <= r.maxAge);
  if (rule && props.tripDuration > rule.maxDays) {
    return {
      all: proxy.$t('searchExtras.travelInsurance.errorMsg.tripDurationError', {
        ageRange: rule.label,
        maxDays: rule.maxDays,
      }),
    };
  }
  return;
};

const createInitialTraveller = (): Traveller => ({
  id: '1',
  dateOfBirth: '',
  isPrimary: true,
  countryOfResidence: {}
});

const resetTravellersToInitial = () => {
  localTravellers.value = [createInitialTraveller()];
  travellerDateValidity.value = { '1': false };
};

// Initialize local travellers from store or create initial
if (travelInsuranceStore.form?.travellers && travelInsuranceStore.form.travellers.length > 0) {
  localTravellers.value = [...travelInsuranceStore.form.travellers];
  localTravellers.value.forEach(traveller => {
    travellerDateValidity.value[traveller.id] = false;
  });
  emit('update:value', localTravellers.value);
} else if (props.travellers.length > 0) {
  localTravellers.value = [...props.travellers];
  localTravellers.value.forEach(traveller => {
    travellerDateValidity.value[traveller.id] = false;
  });
} else {
  resetTravellersToInitial();
}



const validateForm = () => {
  dirty.value = true;
}

const hasTripDurationError = (traveller: Traveller): boolean => {
  return !!showTripDurationError(traveller);
};

const validateTravellers = (): boolean => {
  return localTravellers.value.every(traveller => {
    return (travellerDateValidity.value[traveller.id] || false) && !hasTripDurationError(traveller);
  });
};

const canAddMoreTravellers = computed(() => {
  return localTravellers.value.length < 8;
});

const addTraveller = (): void => {
  if (canAddMoreTravellers.value) {
    const newTravellerId = `traveller_${Date.now()}`;
    localTravellers.value.push({
      id: newTravellerId,
      dateOfBirth: '',
      isPrimary: false
    });
    travellerDateValidity.value[newTravellerId] = false;
    emit('update:value', localTravellers.value);
  }
  datadogRum.addAction('travel_insurance_add_traveller_clicked', {
    product: 'travel_insurance',
    user_action: 'clicked',
    step: 3,
    additional_context: `Total travellers now: ${localTravellers.value.length}`
  });
};

const removeTraveller = (travellerId: string): void => {
  if (localTravellers.value.length > 1) {
    localTravellers.value = localTravellers.value.filter(
      traveller => traveller.id !== travellerId
    );

    const index = submittedTravellers.value.indexOf(travellerId);
    if (index > -1) {
      submittedTravellers.value.splice(index, 1);
    }
    delete travellerDateValidity.value[travellerId];
    emit('update:value', localTravellers.value);

    datadogRum.addAction('travel_insurance_remove_traveller_clicked', {
      product: 'travel_insurance',
      user_action: 'clicked',
      step: 3,
      additional_context: `Total travellers now: ${localTravellers.value.length}`
    });
  }
};

const updateTravellerField = <K extends keyof Traveller>(
  travellerId: string,
  field: K,
  value: Traveller[K]
) => {
  const traveller = localTravellers.value.find(t => t.id === travellerId);
  if (traveller) {
    if (field === 'dateOfBirth') {
      traveller[field] = (value !== null ? value : '') as Traveller[K];
    } else {
      traveller[field] = value;
    }
    emit('update:value', localTravellers.value);
  }
};

const submitTravellers = () => {
  dirty.value = true;
  localTravellers.value.forEach(traveller => {
    if (!submittedTravellers.value.includes(traveller.id)) {
      submittedTravellers.value.push(traveller.id);
    }
  });
};

// section for validation logic
const handleDateValidity = (traveller: Traveller, isValid: boolean): void => {
  travellerDateValidity.value[traveller.id] = isValid;
};

//check if date error needs to show
const shouldShowDateError = (traveller: Traveller): boolean => {
  if (!submittedTravellers.value.includes(traveller.id)) {
    return false;
  }

  return !travellerDateValidity.value[traveller.id] || hasTripDurationError(traveller);
};

const showCountryOfResidenceError = computed(() => {
  const primaryTraveller = localTravellers.value.find(t => t.isPrimary);
  if (!primaryTraveller) {
    return false;
  }
  return dirty.value && (!primaryTraveller.countryOfResidence.name || primaryTraveller.countryOfResidence.name.trim() === '');
});

const handleDateBlur = (travellerId: string): void => {
  if (!submittedTravellers.value.includes(travellerId)) {
    submittedTravellers.value.push(travellerId);
  }
};

function disableTooltip() {
  proxy.$refs.countryTooltip[0]?.disableTooltip();
}

function updateCountryOfResidence(travellerId: string, country: { name: string; id: string }) {
  const traveller = localTravellers.value.find(t => t.id === travellerId);
  if (traveller) {
    traveller.countryOfResidence = country;
    emit('update:value', localTravellers.value);
  }
}

expose({
  handleDateValidity,
  validateTravellers,
  submitTravellers,
  disableTooltip,
  validateForm
});

return { __sfc: true,IconS3, instance, proxy, travelInsuranceStore, countryOptions, props, travellerDateValidity, submittedTravellers, localTravellers, dirty, allCountries, getAgeAtTripEnd, showTripDurationError, createInitialTraveller, resetTravellersToInitial, emit, validateForm, hasTripDurationError, validateTravellers, canAddMoreTravellers, addTraveller, removeTraveller, updateTravellerField, submitTravellers, handleDateValidity, shouldShowDateError, showCountryOfResidenceError, handleDateBlur, disableTooltip, updateCountryOfResidence, DateInput, Tooltip, SingleSelect, IconButton }
}

})